import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  TextField,
  Chip
} from '@mui/material';
import api from '../services/api';
import {
  CheckCircle,
  Download,
  Description,
  Cancel,
  QuestionAnswer
} from '@mui/icons-material';
import SignaturePad from '../components/SignaturePad';

interface AgreementData {
  agreementUrl: string; // Similar to reportUrl in VendorReport
  propertyAddress: string;
  agencyName: string;
  property: {
    address: {
      street: string;
      city: string;
      state: string;
      postalCode: string;
    };
    owner: {
      name: string;
      email: string;
      phone: string;
    } | Array<{ name: string; email?: string; phone?: string }>;
  };
  agency: {
    name: string;
    email: string;
    phone: string;
  };
  vendorSignatures?: Array<{
    vendorName: string;
    vendorEmail?: string;
    signedAt: string;
  }>;
  principalSignatures?: Array<{
    principalName: string;
    principalEmail?: string;
    signedAt: string;
  }>;
  principalResponse?: string; // Rental only: 'Not interested' | 'Need to discuss'
  principalResponseDate?: string;
}

interface VendorAgreementProps {
  agreementType?: 'agency' | 'rental';
}

const VendorAgreement: React.FC<VendorAgreementProps> = ({ agreementType = 'agency' }) => {
  const { token, code } = useParams<{ token?: string; code?: string }>();
  const isRental = agreementType === 'rental';
  const apiBase = isRental ? '/agencies/vendor/rental-agreement' : '/agencies/vendor/agreement';
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email')?.trim() || '';
  const [accessCode, setAccessCode] = useState(code || '');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreementData, setAgreementData] = useState<AgreementData | null>(null);
  const [vendorName, setVendorName] = useState('');
  const [vendorSignature, setVendorSignature] = useState<string | null>(null);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [signedVendors, setSignedVendors] = useState<Array<{ vendorName: string; vendorEmail?: string; signedAt: string }>>([]);
  const [currentVendorSigned, setCurrentVendorSigned] = useState(false);
  const [responseSubmitting, setResponseSubmitting] = useState(false);

  // Normalize signatures for display (rental returns principalSignatures, agency returns vendorSignatures)
  const normalizeSignatures = (data: any): Array<{ vendorName: string; vendorEmail?: string; signedAt: string }> => {
    if (isRental && data.principalSignatures && Array.isArray(data.principalSignatures)) {
      return data.principalSignatures.map((s: any) => ({
        vendorName: s.principalName,
        vendorEmail: s.principalEmail,
        signedAt: typeof s.signedAt === 'string' ? s.signedAt : (s.signedAt?.toISOString?.() || ''),
      }));
    }
    return data.vendorSignatures || [];
  };

  // Parse vendor names from owner name
  const parseVendorNames = (ownerName: string): string[] => {
    if (!ownerName) return [];
    const separators = [',', ' and ', ' & ', ' AND '];
    for (const sep of separators) {
      if (ownerName.includes(sep)) {
        return ownerName.split(sep).map(n => n.trim()).filter(n => n);
      }
    }
    return [ownerName.trim()];
  };

  // Deduplicate signatures by email (or name if no email), keeping the latest one
  const deduplicateSignatures = (signatures: Array<{ vendorName: string; vendorEmail?: string; signedAt: string | Date }>): Array<{ vendorName: string; vendorEmail?: string; signedAt: string }> => {
    const signatureMap = new Map<string, { vendorName: string; vendorEmail?: string; signedAt: string }>();
    
    // Sort by signedAt (newest first) to ensure we keep the latest
    const sorted = [...signatures].sort((a, b) => {
      const dateA = new Date(a.signedAt).getTime();
      const dateB = new Date(b.signedAt).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
    
    for (const sig of sorted) {
      // Use email as key if available, otherwise use name
      const key = sig.vendorEmail 
        ? sig.vendorEmail.trim().toLowerCase()
        : sig.vendorName.trim().toLowerCase();
      
      // Only add if we haven't seen this key yet (since sorted newest first, first one is latest)
      if (!signatureMap.has(key)) {
        signatureMap.set(key, {
          vendorName: sig.vendorName,
          vendorEmail: sig.vendorEmail,
          signedAt: typeof sig.signedAt === 'string' ? sig.signedAt : sig.signedAt.toISOString(),
        });
      }
    }
    
    // Convert map values back to array and sort by signedAt (newest first) for display
    return Array.from(signatureMap.values()).sort((a, b) => {
      const dateA = new Date(a.signedAt).getTime();
      const dateB = new Date(b.signedAt).getTime();
      return dateB - dateA; // Descending order (newest first)
    });
  };

  const handleUnlock = async () => {
    if (!token) return;
    if (!accessCode || accessCode.length !== 6) {
      setError('Please enter a 6-digit access code');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post(`${apiBase}/${token}`, { code: accessCode });
      const signatures = normalizeSignatures(data);
      const transformedData: AgreementData = {
        agreementUrl: data.agreementUrl || data.agreement?.url || '',
        propertyAddress: data.propertyAddress || (data.property?.address
          ? `${data.property.address.street || ''}, ${data.property.address.city || ''}, ${data.property.address.state || ''} ${data.property.address.postalCode || ''}`.trim()
          : 'Property Address'),
        agencyName: data.agencyName || data.agency?.name || 'Agency',
        property: data.property || {
          address: { street: '', city: '', state: '', postalCode: '' },
          owner: { name: '', email: '', phone: '' }
        },
        agency: data.agency || { name: '', email: '', phone: '' },
        vendorSignatures: signatures,
        principalSignatures: data.principalSignatures,
        principalResponse: data.principalResponse,
        principalResponseDate: data.principalResponseDate,
      };
      setAgreementData(transformedData);
      if (signatures.length > 0) {
        const deduplicated = deduplicateSignatures(signatures);
        setSignedVendors(deduplicated);
      }
      
      // Prefill vendor name: from URL email if present (per-vendor link), else first vendor
      const owner = transformedData.property.owner;
      const ownersList = Array.isArray(owner) ? owner : (owner?.name ? [owner] : []);
      if (emailFromUrl && ownersList.length > 0) {
        const matched = ownersList.find(
          (o: any) => o.email && String(o.email).trim().toLowerCase() === emailFromUrl.toLowerCase()
        );
        if (matched?.name) {
          setVendorName(matched.name.trim());
        } else {
          const vendorNames = parseVendorNames(Array.isArray(owner) ? owner.map((o: any) => o.name).filter(Boolean).join(', ') : (owner as any)?.name || '');
          if (vendorNames.length > 0) setVendorName(vendorNames[0]);
        }
      } else {
        const ownerName = Array.isArray(owner) ? owner.map((o: any) => o.name).filter(Boolean).join(', ') : (owner as any)?.name || '';
        const vendorNames = parseVendorNames(ownerName);
        if (vendorNames.length > 0) setVendorName(vendorNames[0]);
      }
    } catch (err: any) {
      console.error('Error fetching agreement:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load agency agreement');
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch if code is in URL
  useEffect(() => {
    if (code && code.length === 6 && token) {
      handleUnlock();
    } else if (!token && !code) {
      setError('Invalid access token or code');
    }
  }, [code, token]);

  const handleSubmitSignature = async () => {
    if (!vendorName || !vendorName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!vendorSignature) {
      setError('Please provide your signature');
      return;
    }
    if (!token) {
      setError('Invalid access token');
      return;
    }

    setError(null);
    setSubmitting(true);
    setSignatureSaving(true);

    try {
      // Extract base64 data from data URL
      const base64Data = vendorSignature.includes(',') 
        ? vendorSignature.split(',')[1] 
        : vendorSignature;
      
      // Get vendor email from property owner list if available
      const owners = Array.isArray(agreementData.property.owner) 
        ? agreementData.property.owner 
        : [agreementData.property.owner];
      const matchedOwner = owners.find((o: any) => 
        o.name && o.name.trim().toLowerCase() === vendorName.trim().toLowerCase()
      );
      const vendorEmail = matchedOwner?.email;
      
      if (isRental) {
        await api.post(`${apiBase}/${token}/sign`, {
          code: accessCode,
          principalName: vendorName.trim(),
          principalEmail: vendorEmail,
          signatureImageData: base64Data,
          signatureImageType: 'image/png',
        });
      } else {
        await api.post(`${apiBase}/${token}/sign`, {
          code: accessCode,
          vendorName: vendorName.trim(),
          vendorEmail: vendorEmail,
          signatureImageData: base64Data,
          signatureImageType: 'image/png',
        });
      }
      setCurrentVendorSigned(true);

      const { data: refreshData } = await api.post(`${apiBase}/${token}`, { code: accessCode });
      const refreshSigs = normalizeSignatures(refreshData);
      if (refreshSigs.length > 0) {
        const deduplicated = deduplicateSignatures(refreshSigs);
        setSignedVendors(deduplicated);
      }
      const transformedData: AgreementData = {
        agreementUrl: refreshData.agreementUrl || refreshData.agreement?.url || agreementData?.agreementUrl || '',
        propertyAddress: refreshData.propertyAddress || agreementData?.propertyAddress || '',
        agencyName: refreshData.agencyName || refreshData.agency?.name || agreementData?.agencyName || '',
        property: refreshData.property || agreementData?.property || {
          address: { street: '', city: '', state: '', postalCode: '' },
          owner: { name: '', email: '', phone: '' }
        },
        agency: refreshData.agency || agreementData?.agency || { name: '', email: '', phone: '' },
        vendorSignatures: refreshSigs,
        principalSignatures: refreshData.principalSignatures,
        principalResponse: refreshData.principalResponse ?? agreementData?.principalResponse,
        principalResponseDate: refreshData.principalResponseDate ?? agreementData?.principalResponseDate,
      };
      setAgreementData(transformedData);
    } catch (err: any) {
      console.error('Error submitting signature:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to submit signature';
      setError(msg);
      if (isRental && err.response?.status === 400 && err.response?.data?.alreadySigned) {
        setCurrentVendorSigned(true);
        try {
          const { data: refreshData } = await api.post(`${apiBase}/${token}`, { code: accessCode });
          const refreshSigs = normalizeSignatures(refreshData);
          if (refreshSigs.length > 0) setSignedVendors(deduplicateSignatures(refreshSigs));
          setAgreementData(prev => prev && refreshData ? {
            ...prev,
            principalSignatures: refreshData.principalSignatures,
            principalResponse: refreshData.principalResponse,
            principalResponseDate: refreshData.principalResponseDate,
          } : prev);
        } catch (_) { /* ignore */ }
      }
    } finally {
      setSubmitting(false);
      setSignatureSaving(false);
    }
  };

  const handleDownload = () => {
    if (agreementData?.agreementUrl) {
      window.open(agreementData.agreementUrl, '_blank');
    }
  };

  const handleAgreementResponse = async (response: 'Not interested' | 'Need to discuss') => {
    if (!token || !accessCode || accessCode.length !== 6 || responseSubmitting || agreementData?.principalResponse) return;
    setError(null);
    setResponseSubmitting(true);
    try {
      await api.post(`${apiBase}/${token}/response`, { code: accessCode, response });
      setAgreementData(prev => prev ? {
        ...prev,
        principalResponse: response,
        principalResponseDate: new Date().toISOString(),
      } : null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to submit response');
    } finally {
      setResponseSubmitting(false);
    }
  };

  // Check if current vendor has already signed (match by email if available, otherwise by name)
  useEffect(() => {
    if (vendorName && signedVendors.length > 0 && agreementData) {
      // Get vendor email from property owner list if available
      const owners = Array.isArray(agreementData.property.owner) 
        ? agreementData.property.owner 
        : [agreementData.property.owner];
      const matchedOwner = owners.find((o: any) => 
        o.name && o.name.trim().toLowerCase() === vendorName.trim().toLowerCase()
      );
      const vendorEmail = matchedOwner?.email;
      
      const hasSigned = signedVendors.some(sig => {
        // Match by email if both have emails
        if (vendorEmail && sig.vendorEmail) {
          return sig.vendorEmail.trim().toLowerCase() === vendorEmail.trim().toLowerCase();
        }
        // Fallback to name matching
        return sig.vendorName.trim().toLowerCase() === vendorName.trim().toLowerCase();
      });
      setCurrentVendorSigned(hasSigned);
    }
  }, [vendorName, signedVendors, agreementData]);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!agreementData) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h5" gutterBottom align="center">
              {isRental ? 'Rental Management Agreement Access' : 'Agency Agreement Access'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mt: 3 }}>
              <TextField
                fullWidth
                label="Access Code"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="Enter 6-digit code"
                inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                sx={{ mb: 2 }}
              />
              <Button
                fullWidth
                variant="contained"
                onClick={handleUnlock}
                disabled={!accessCode || accessCode.length !== 6}
              >
                Access Agreement
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const vendorNames = parseVendorNames(agreementData.property.owner.name);
  // Get owner emails for matching
  const owners = Array.isArray(agreementData.property.owner) 
    ? agreementData.property.owner 
    : [agreementData.property.owner];
  
  const allVendorsSigned = vendorNames.length > 0 && vendorNames.every(name => {
    const matchedOwner = owners.find((o: any) => 
      o.name && o.name.trim().toLowerCase() === name.trim().toLowerCase()
    );
    const ownerEmail = matchedOwner?.email;
    
    return signedVendors.some(sig => {
      // Match by email if both have emails
      if (ownerEmail && sig.vendorEmail) {
        return sig.vendorEmail.trim().toLowerCase() === ownerEmail.trim().toLowerCase();
      }
      // Fallback to name matching
      return sig.vendorName.trim().toLowerCase() === name.trim().toLowerCase();
    });
  });

  const hasPrincipalResponse = isRental && Boolean(agreementData.principalResponse);

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Description sx={{ mr: 1, fontSize: 32 }} />
            <Typography variant="h5">
              {isRental ? 'Rental Management Agreement' : 'Agency Agreement'}
            </Typography>
          </Box>
          <Divider sx={{ my: 2 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {allVendorsSigned && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {isRental ? 'All principals have signed the agreement.' : 'All vendors have signed the agreement.'}
            </Alert>
          )}

          {/* Agreement PDF Viewer - following VendorReport pattern */}
          {agreementData && (
            <Box sx={{ mb: 3, textAlign: 'center' }}>
              <iframe
                src={agreementData.agreementUrl}
                width="100%"
                style={{ 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  minHeight: 'calc(100vh - 400px)',
                  height: 'calc(100vh - 400px)',
                  maxHeight: '1200px'
                }}
                title={isRental ? 'Rental Management Agreement' : 'Agency Agreement'}
              />
            </Box>
          )}

          {/* Download Button - following VendorReport pattern */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            {agreementData && (
              <Button
                variant="outlined"
                startIcon={<Download />}
                href={agreementData.agreementUrl}
                target="_blank"
                download
              >
                Download Agreement PDF
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Signed Vendors/Principals Status */}
          {signedVendors.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {isRental ? 'Signed Principals:' : 'Signed Vendors:'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {signedVendors.map((sig, index) => (
                  <Chip
                    key={index}
                    icon={<CheckCircle />}
                    label={`${sig.vendorName}${sig.vendorEmail ? ` (${sig.vendorEmail})` : ''} - ${new Date(sig.signedAt).toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                    color="success"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Reject / Discuss further (rental only; hidden once this principal has signed) */}
          {isRental && !allVendorsSigned && !currentVendorSigned && (
            <Box sx={{ mb: 3 }}>
              {agreementData?.principalResponse ? (
                <Alert severity={agreementData.principalResponse === 'Not interested' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  <Typography variant="body1" fontWeight="bold">
                    Your response: {agreementData.principalResponse}
                  </Typography>
                  {agreementData.principalResponseDate && (
                    <Typography variant="body2" color="text.secondary">
                      Submitted on {new Date(agreementData.principalResponseDate).toLocaleString()}
                    </Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={async () => {
                        if (!token || !accessCode || accessCode.length !== 6 || responseSubmitting) return;
                        setError(null);
                        setResponseSubmitting(true);
                        try {
                          await api.post(`${apiBase}/${token}/response`, { code: accessCode, response: null });
                          setAgreementData(prev =>
                            prev
                              ? {
                                  ...prev,
                                  principalResponse: undefined,
                                  principalResponseDate: undefined,
                                }
                              : null
                          );
                        } catch (err: any) {
                          setError(err.response?.data?.error || err.message || 'Failed to clear response');
                        } finally {
                          setResponseSubmitting(false);
                        }
                      }}
                    >
                      Cancel response
                    </Button>
                  </Box>
                </Alert>
              ) : (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    You can respond without signing:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="medium"
                      startIcon={<Cancel />}
                      onClick={() => handleAgreementResponse('Not interested')}
                      disabled={responseSubmitting}
                    >
                      {responseSubmitting ? 'Submitting...' : 'Not interested'}
                    </Button>
                    <Button
                      variant="outlined"
                      color="info"
                      size="medium"
                      startIcon={<QuestionAnswer />}
                      onClick={() => handleAgreementResponse('Need to discuss')}
                      disabled={responseSubmitting}
                    >
                      {responseSubmitting ? 'Submitting...' : 'Need to discuss'}
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Signature Section */}
          {!allVendorsSigned && !hasPrincipalResponse && (
            <>
              <Typography variant="h6" gutterBottom>
                Sign Agreement
              </Typography>
              
              {currentVendorSigned ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  You have already signed this agreement.
                </Alert>
              ) : (
                <>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      value={vendorName}
                      onChange={(e) => setVendorName(e.target.value)}
                      placeholder="Enter your full name"
                      required
                      disabled={currentVendorSigned}
                    />
                    {vendorNames.length > 1 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {isRental ? 'Expected principals: ' : 'Expected vendors: '}{vendorNames.join(', ')}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body1" gutterBottom>
                      Please sign below:
                    </Typography>
                    <SignaturePad
                      width={500}
                      height={200}
                      initialSignature={null}
                      saving={signatureSaving}
                      onSave={(dataUrl) => {
                        setVendorSignature(dataUrl);
                      }}
                      onClear={() => {
                        setVendorSignature(null);
                      }}
                    />
                  </Box>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSubmitSignature}
                    disabled={!vendorName || !vendorSignature || submitting || currentVendorSigned}
                    size="large"
                  >
                    {submitting ? 'Submitting...' : 'Sign & Approve Agreement'}
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
};

export default VendorAgreement;

